import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Modal, Button } from 'react-bootstrap';
import { startCase } from 'lodash';

import glossary from './glossary';
import { useProfile } from '../shared/hooks';
import LogoSquared from '../../assets/images/logo-squared.png';

const SideLink = ({ title, path, subtitle }) => (
  <li>
    <Link className="link--negative" to={path}>
      {title} {subtitle && <span className="subtitle">{subtitle}</span>}
    </Link>
  </li>
);

const ExternalSideLink = ({ title, href, special, pivot }) => (
  <li>
    <a
      className={`${special ? 'link--special' : 'link--negative'} ${pivot &&
        'link--pivot'}`}
      href={href}
      alt={title}
      target="_blank"
      rel="noreferrer noopener"
    >
      {title}
    </a>
  </li>
);

const GlossaryEntry = ({ acronym, term, meaning, formula }) => (
  <div>
    <p>
      <span className="sidebar__glossary--acronym">{acronym}</span>
      <span className="sidebar__glossary--title">{term}</span>
    </p>
    <p className="sidebar__glossary--body">{meaning}</p>
    <pre className="sidebar__glossary--formula">{formula}</pre>
  </div>
);

const Glossary = () => {
  const [show, setShow] = useState(false);

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  return (
    <>
      <button
        className="link link--negative"
        onClick={handleShow}
        data-testid="main-menu-terms"
      >
        Glossary
      </button>

      <Modal show={show} onHide={handleClose} side="lg">
        <Modal.Header>
          <Modal.Title>Glossary</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div class="sidebar__glossary">
            {glossary.map(entry => (
              <GlossaryEntry key={entry.acronym} {...entry} />
            ))}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

const Sidebar = ({ onSignOut, showTerms, toggleShowTerms }) => {
  const profile = useProfile();

  return (
    <nav data-testid="sidebar" className="sidebar">
      <a className="sidebar__logo" href="/">
        <img
          src={LogoSquared}
          alt="Pivot Analysis Logo"
          title="Pivot Analysis Logo"
        />
      </a>
      <button
        className="menu__toggle collapsed"
        type="button"
        data-toggle="collapse"
        data-target="#menu"
      >
        <div className="menu__toggle__icon" />
      </button>
      <div className="menu collapse" id="menu">
        <div className="menu__block">
          <ul className="menu__links">
            <SideLink title="Teams" path="/teams" />
            <SideLink title="Players" path="/players" />
            <SideLink title="Team Rankings" path="/stats" />
          </ul>
        </div>

        <div className="menu__block">
          <p className="menu__title">About</p>
          <ul className="menu__links menu__links--is-small">
            <ExternalSideLink
              title="Help"
              href="https://www.pivotanalysis.com/pivotapp-help"
            />
            <ExternalSideLink
              title="Contact Us"
              href="mailto:contact@pivotanalysis.com"
            />
            <ExternalSideLink
              title="FAQ"
              href="https://www.pivotanalysis.com/pivotapp-faq"
            />

            <li>
              <Glossary show={showTerms} toggleShow={toggleShowTerms} />
            </li>
          </ul>
          <div className="menu__spacer" />
          <p className="menu__title">
            Profile <br />
            {profile?.name} | {startCase(profile?.tier)} Tier
          </p>
          <ul className="menu__links menu__links--is-small">
            <ExternalSideLink
              title="Upgrade &#127881;"
              href="https://www.pivotanalysis.com/request-a-quote"
              special={true}
            />
            <ExternalSideLink
              title="Forgot Password"
              href="https://api.pivotanalysis.app/password_reset"
            />
            <li>
              <button
                className="link link--negative"
                onClick={onSignOut}
                data-testid="main-menu-logout"
              >
                Sign Out
              </button>
            </li>
          </ul>
        </div>

        <div className="menu__block">
          <p className="menu__title">Other Pivot Apps</p>
          <ul className="menu__links menu__links--is-small">
            <ExternalSideLink
              title="Matchup Machine"
              href="https://mm.pivotanalysis.app"
              special={true}
            />
          </ul>
        </div>
      </div>
    </nav>
  );
};

export default Sidebar;

Sidebar.propTypes = {
  onSignOut: PropTypes.func.isRequired,
};
